import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";
import { menuItems, MenuItem } from "../common/MenuItems";

/**
 * Global / omni search — one backend endpoint that joins five collections
 * (folders, files, workflows, users, notifications) into a single grouped
 * result. Replaces the older "fan-out 5 parallel HTTP calls from the
 * browser" approach which was fragile because not every list endpoint
 * supports the same search shape.
 *
 * Backend route: GET /dms-v2-srv/global-search-srv?q=…&limit=5
 * SDK service:   GlobalSearchService.search()
 */

export type SearchHitKind =
  | "page"
  | "folder"
  | "file"
  | "workflow"
  | "user"
  | "notification";

export interface GlobalSearchHit {
  kind: SearchHitKind;
  id: string;
  title: string;
  subtitle?: string;
  href: string;
  /** Some hits carry extra fields (e.g. file.document_store_id) for routing. */
  [k: string]: any;
}

export interface GlobalSearchResult {
  pages: GlobalSearchHit[];
  folders: GlobalSearchHit[];
  files: GlobalSearchHit[];
  workflows: GlobalSearchHit[];
  users: GlobalSearchHit[];
  notifications: GlobalSearchHit[];
  total: number;
  query: string;
}

const EMPTY: GlobalSearchResult = {
  pages: [],
  folders: [],
  files: [],
  workflows: [],
  users: [],
  notifications: [],
  total: 0,
  query: "",
};

/**
 * Flatten the sidebar's menu tree into a single search-friendly list,
 * carrying the parent crumb as a subtitle ("User Management › Groups").
 * Skips entries without a path (pure parent containers).
 */
interface FlatMenu {
  title: string;
  path: string;
  parentTitle?: string;
}
function flattenMenu(items: MenuItem[], parentTitle?: string): FlatMenu[] {
  const out: FlatMenu[] = [];
  for (const item of items) {
    if (item.path) {
      out.push({ title: item.title, path: item.path, parentTitle });
    }
    if (item.children && item.children.length) {
      out.push(...flattenMenu(item.children, item.title));
    }
  }
  return out;
}

/**
 * Filter the flattened menu against the query. Case-insensitive substring
 * match on title + parent title. Returns up to `limit` hits.
 */
function searchPagesLocal(query: string, limit: number): GlobalSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const flat = flattenMenu(menuItems);
  const hits = flat
    .filter((m) => {
      const t = m.title.toLowerCase();
      const p = (m.parentTitle || "").toLowerCase();
      return t.includes(q) || p.includes(q);
    })
    // Surface exact / starts-with matches first.
    .sort((a, b) => {
      const aT = a.title.toLowerCase();
      const bT = b.title.toLowerCase();
      const aStarts = aT.startsWith(q) ? 0 : 1;
      const bStarts = bT.startsWith(q) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return aT.localeCompare(bT);
    })
    .slice(0, limit)
    .map((m) => ({
      kind: "page" as const,
      id: m.path,
      title: m.title,
      subtitle: m.parentTitle ? `in ${m.parentTitle}` : undefined,
      href: m.path,
    }));
  return hits;
}

class GlobalSearchService {
  /**
   * Run the omni search.
   * @param query         user query string
   * @param limitPerKind  max hits per section (1-25). Default 5.
   */
  async search(query: string, limitPerKind = 5): Promise<GlobalSearchResult> {
    const q = query.trim();
    if (!q) return EMPTY;

    // Pages are matched client-side from the sidebar config — no
    // backend call needed. We resolve them first so they always
    // appear, even if the backend request fails.
    const pages = searchPagesLocal(q, limitPerKind);

    try {
      const res = await apiClient.get<any>(
        `${API_ENDPOINTS.GLOBAL_SEARCH.SEARCH}?q=${encodeURIComponent(q)}&limit=${limitPerKind}`,
      );
      const body = res.data?.data ?? {};
      const folders       = Array.isArray(body.folders)       ? body.folders       : [];
      const files         = Array.isArray(body.files)         ? body.files         : [];
      const workflows     = Array.isArray(body.workflows)     ? body.workflows     : [];
      const users         = Array.isArray(body.users)         ? body.users         : [];
      const notifications = Array.isArray(body.notifications) ? body.notifications : [];
      const backendTotal  = typeof body.total === "number" ? body.total : 0;
      return {
        pages,
        folders, files, workflows, users, notifications,
        total: pages.length + backendTotal,
        query: typeof body.query === "string" ? body.query : q,
      };
    } catch (error) {
      console.error("Global search failed", error);
      // Even if the backend bombed, return the local page matches so
      // the user can still navigate the menu via the search bar.
      return { ...EMPTY, pages, total: pages.length, query: q };
    }
  }
}

export const globalSearchService = new GlobalSearchService();
export default globalSearchService;
